import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, authHeader } from '../lib/api'
import type { StudentBookmark, RecruiterBookmark } from '@tnf-recruit/shared'
import { useAuth } from './useAuth'

// =============================================================================
// Student bookmarks (student → school)
// =============================================================================

export function useStudentBookmarks() {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()

  const bookmarks = useQuery({
    queryKey: ['studentBookmarks'],
    queryFn: async () => {
      const token = await getAccessToken()
      const { data } = await apiClient.get('/bookmarks/student', authHeader(token))
      return data.data as (StudentBookmark & { schools?: any })[]
    },
  })

  const addBookmark = useMutation({
    mutationFn: async (body: { schoolId: string; notes?: string }) => {
      const token = await getAccessToken()
      const { data } = await apiClient.post('/bookmarks/student', body, authHeader(token))
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studentBookmarks'] })
    },
  })

  const removeBookmark = useMutation({
    mutationFn: async (bookmarkId: string) => {
      const token = await getAccessToken()
      await apiClient.delete(`/bookmarks/student/${bookmarkId}`, authHeader(token))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studentBookmarks'] })
    },
  })

  return { bookmarks, addBookmark, removeBookmark }
}

// =============================================================================
// Recruiter bookmarks (recruiter → student)
// =============================================================================

export function useRecruiterBookmarks() {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()

  const bookmarks = useQuery({
    queryKey: ['recruiterBookmarks'],
    queryFn: async () => {
      const token = await getAccessToken()
      const { data } = await apiClient.get('/bookmarks/recruiter', authHeader(token))
      return data.data as (RecruiterBookmark & { studentProfiles?: any })[]
    },
  })

  const addBookmark = useMutation({
    mutationFn: async (body: { studentId: string; notes?: string }) => {
      const token = await getAccessToken()
      const { data } = await apiClient.post('/bookmarks/recruiter', body, authHeader(token))
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recruiterBookmarks'] })
      queryClient.invalidateQueries({ queryKey: ['recruiterDashboard'] })
    },
  })

  const removeBookmark = useMutation({
    mutationFn: async (bookmarkId: string) => {
      const token = await getAccessToken()
      await apiClient.delete(`/bookmarks/recruiter/${bookmarkId}`, authHeader(token))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recruiterBookmarks'] })
      queryClient.invalidateQueries({ queryKey: ['recruiterDashboard'] })
    },
  })

  return { bookmarks, addBookmark, removeBookmark }
}
